import {
	addCoFConfig,
	getRiskConfig,
	saveCoF,
	setCoFConfig,
	setCoFIsChanged,
	setRefreshList,
	setTab,
} from '@Actions';
import { countValuesNeedingScore } from '@Components/RiskSetup/templates/countValuesNeedingScore';
import { useLoadCoverage } from '@Components/Category/useComponentCoverage';
import { NavigationPrompt } from '@innovyze/stylovyze';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
} from '@mui/material';
import { selectCofStore } from '@Selectors/CoF.selectors';
import { selectRiskSetting } from '@Selectors/Risk.selectors';
import {
	selectUnavailableComponentsForSection,
	selectUnrecognisedValuesForSection,
} from '@Selectors/RiskTemplateApply.selectors';
import { ComponentProps, NamespacedStoreState } from '@Types';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGlobalization } from '@Translations/useGlobalization';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { distributeToHundred } from '@Utils/distributeToHundred';
import { dropUnavailableComponents } from '@Utils/dropUnavailableComponents';
import { escapeAttributeValue } from '@Utils/cssSelector';
import {
	dismissMissingSummary,
	isMissingSummaryDismissed,
} from '@Utils/templateBanner.storage';
import { MissingComponentsBanner } from '@Components/RiskSetup/templates/MissingComponentsBanner.component';
import { SetupCategoriesSkeleton } from '@Components/RiskSetup/templates/SetupCategoriesSkeleton.component';
import { CategoryWeightBanner } from '@Components/Category/CategoryWeightBanner.component';
import { ModelDonut } from '@Components/Category/ModelDonut.component';
import { ClassicCoFCategories } from './ClassicCoFCategories';
import { CoFSetupIntro } from './CoFSetupIntro.component';
import { NoCoFCategories } from './NoCoFCategories';
import { RenderedCoFCategories } from './RenderedCoFCategories';

export const CoFSetup = ({
	onAddComponent,
	onEditComponent,
}: ComponentProps) => {
	const { t } = useGlobalization();
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();
	const [open, setOpen] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [validCategoryName, setValidCategoryName] = useState<string>('');

	const dispatch = useDispatch();
	const history = useHistory();
	const riskSettings = selectRiskSetting();
	useLoadCoverage(riskSettings.configId, isRiskTemplatesEnabled);
	const cofStore = selectCofStore();
	const categories = cofStore?.categories || [];
	const unavailable = useSelector((state: NamespacedStoreState) =>
		selectUnavailableComponentsForSection(state, 'cof'),
	);
	const unrecognisedValues = useSelector((state: NamespacedStoreState) =>
		selectUnrecognisedValuesForSection(state, 'cof'),
	);
	const showSkeleton =
		cofStore?.isLoadingCategories && categories.length === 0;
	const [missingSummaryDismissed, setMissingSummaryDismissed] = useState(
		isMissingSummaryDismissed(riskSettings.configId, 'cof'),
	);

	useEffect(() => {
		dispatch(setTab('cof'));
	}, []);

	const totalWeight = categories
		.map(cat => cat.weighting)
		.reduce((pre, current) => pre + current, 0);

	// One click: scale the category weights proportionally to total 100%
	// (even split if none set yet) — same helper as LoF. Keyed by object
	// reference, not name, so two categories that happen to share a name
	// (allowed in the classic path) don't collide and silently drop one.
	const balanceCategories = () => {
		if (categories.length === 0) return;
		const rounded = distributeToHundred(
			categories.map(c => c.weighting || 0),
		);
		const weightByCategory = new Map(
			categories.map((c, i) => [c, rounded[i]]),
		);
		dispatch(
			setCoFConfig(
				categories.map(c =>
					weightByCategory.has(c)
						? { ...c, weighting: weightByCategory.get(c) as number }
						: c,
				),
			),
		);
	};

	const handleAddCategoryClicked = () => {
		if (!showSkeleton) setOpen(true);
	};

	const handleClose = () => {
		setNewCategoryName('');
		setValidCategoryName('');
		setOpen(false);
	};

	const handleAdd = () => {
		if (newCategoryName !== '') {
			dispatch(
				addCoFConfig({
					name: newCategoryName,
					isCondition: false,
					useThisOnly: false,
					weighting: 0,
					useHighestScore: false,
					components: [],
				}),
			);
			setOpen(false);
			setNewCategoryName('');
			setValidCategoryName('');
		}
	};

	const validateCategoryName = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.value.toUpperCase();
		let valid = true;
		valid =
			cofStore?.categories?.find(
				category => category.name.toUpperCase() == name,
			) === undefined;
		setValidCategoryName(valid ? '' : t('Enter a unique name'));
		setNewCategoryName(valid ? event.target.value : '');
	};

	// Reads cofStore.categories directly (not the `|| []`-defaulted
	// `categories` above) so a store with no categories field at all is
	// still treated as invalid, matching the original behaviour.
	const isConfigValid = () => {
		const cats = cofStore.categories;
		if (!cats) return false;
		const categoriesTotalWeight = cats
			.map(category => category.weighting)
			.reduce((prev, current) => prev + current, 0);
		if (categoriesTotalWeight !== 100 && cats.length > 0) return false;
		for (const category of cats) {
			if (
				!category.useHighestScore &&
				category.components &&
				category.components.length != 0
			) {
				const total = category.components
					.map(component =>
						component.select ? component.weighting : 0,
					)
					.reduce((prev, current) => prev + current, 0);

				// We want a total of 0 or 100 to be valid
				return total === 0 || total === 100;
			}
		}

		return true;
	};

	const navMsg = isConfigValid()
		? t(
				'You have modified your COF configuration and have unsaved changes.  You can save your configuration changes, discard changes, or cancel to continue editing.',
		  )
		: t(
				'You have modified your COF configuration and have unsaved changes.  You can discard changes, or cancel to continue editing.',
		  );

	const refresh = () => {
		dispatch(getRiskConfig(riskSettings.configId));
	};

	const onContinueWithoutMissingComponents = () => {
		const cofDrop = unavailable.map(item => ({
			categoryName: item.categoryName,
			componentName: item.componentName,
		}));
		dispatch(setCoFConfig(dropUnavailableComponents(categories, cofDrop)));
		dispatch(saveCoF({ refresh }));
		dismissMissingSummary(riskSettings.configId, 'cof');
		setMissingSummaryDismissed(true);
	};

	const onReviewMissingComponents = () => {
		const first = unavailable[0];
		if (first) {
			document
				.querySelector(
					`[data-cy="component-${escapeAttributeValue(
						first.componentName,
					)}"][data-category="${escapeAttributeValue(
						first.categoryName,
					)}"]`,
				)
				?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};

	const missingComponentsBanner = isRiskTemplatesEnabled &&
		!missingSummaryDismissed &&
		(unavailable.length > 0 || unrecognisedValues.length > 0) && (
			<Grid item xs={12}>
				<MissingComponentsBanner
					section="cof"
					unavailableComponents={unavailable}
					unrecognisedValues={unrecognisedValues}
					valuesNeedingScore={countValuesNeedingScore(
						categories,
						unrecognisedValues,
					)}
					onContinue={onContinueWithoutMissingComponents}
					onReview={onReviewMissingComponents}
					onDismiss={() => setMissingSummaryDismissed(true)}
					configId={riskSettings.configId}
				/>
			</Grid>
		);

	return (
		<>
			<NavigationPrompt
				when={cofStore.isChanged}
				navigate={nextLocation => {
					history.push(nextLocation);
				}}
				confirmText={isConfigValid() ? t('Save') : t('Discard')}
				onNavigateAway={() => {
					if (isConfigValid()) {
						dispatch(saveCoF({ refresh: refresh }));
						dispatch(setRefreshList(true));
					} else {
						dispatch(getRiskConfig(riskSettings.configId));
						dispatch(setCoFIsChanged(false));
					}
				}}
				secondaryConfirmText={
					isConfigValid() ? t('Discard') : undefined
				}
				onSecondaryNavigateAway={() => {
					dispatch(getRiskConfig(riskSettings.configId));
					dispatch(setCoFIsChanged(false));
				}}
				shouldBlockNavigation={() => true}
				shouldBlockReload={cofStore.isChanged}
				content={navMsg}></NavigationPrompt>
			{isRiskTemplatesEnabled ? (
				<>
					<CoFSetupIntro />
					{!showSkeleton && categories.length > 0 && (
						<CategoryWeightBanner
							totalWeight={totalWeight}
							onBalance={balanceCategories}
						/>
					)}
					<Grid container spacing={3}>
						<Grid item xs={12} md={8} lg={9} container>
							{showSkeleton ? (
								<SetupCategoriesSkeleton />
							) : (
								<>
									{missingComponentsBanner}
									{categories.length === 0 ? (
										<NoCoFCategories
											handleAddCategoryClicked={
												handleAddCategoryClicked
											}
										/>
									) : (
										<RenderedCoFCategories
											categories={categories}
											totalWeight={totalWeight}
											onAddComponent={onAddComponent}
											onEditComponent={onEditComponent}
											handleAddCategoryClicked={
												handleAddCategoryClicked
											}
										/>
									)}
								</>
							)}
						</Grid>
						{!showSkeleton && categories.length > 0 && (
							<Grid item xs={12} md={4} lg={3}>
								<Box sx={{ position: 'sticky', top: 16 }}>
									<ModelDonut
										categories={categories}
										axisLabel={t('CoF')}
									/>
								</Box>
							</Grid>
						)}
					</Grid>
				</>
			) : (
				<ClassicCoFCategories
					showSkeleton={showSkeleton}
					categories={categories}
					totalWeight={totalWeight}
					onAddComponent={onAddComponent}
					onEditComponent={onEditComponent}
					handleAddCategoryClicked={handleAddCategoryClicked}
				/>
			)}
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="form-dialog-title">
				<DialogTitle id="form-dialog-title" style={{ width: '481px' }}>
					Add Category
				</DialogTitle>
				<DialogContent>
					<TextField
						margin="dense"
						id="name"
						label={t('Category Name')}
						type="text"
						fullWidth
						variant="outlined"
						defaultValue={newCategoryName}
						helperText={validCategoryName}
						error={validCategoryName !== ''}
						onChange={validateCategoryName}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						variant="contained">
						{t('Cancel')}
					</Button>
					<Button
						data-testid="cof-setup-add-category-dialog-add-category-btn"
						onClick={handleAdd}
						color="primary"
						variant="contained">
						{t('Add Category')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
